import React, { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import { AxisDateTimeFull } from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import EmailModal from "../modals/EmailModal";
import { ShiftLinkExplicit } from "../util/Links";
import DateRangeSelectModal from "../modals/DateRangeSelectModal";
import moment from "moment";
import { PageTitleController } from "../components/PageTitleController";
import { BsMailbox2Flag } from "react-icons/bs";
import { addDays } from "date-fns";

const EmailTable = () => {
  const historyKey = "EmailTable";

  let history = localStorage.key("history")
    ? JSON.parse(localStorage.getItem("history"))
    : null;
  if (history !== null && historyKey in history)
    history = JSON.parse(history[historyKey]);
  else {
    history = null;
  }

  if (
    history !== null &&
    history?.lastUpdated !== undefined &&
    new Date() - new Date(history.lastUpdated) > 600000
  ) {
    localStorage.removeItem("history");
    history = null;
  }

  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(history ? history?.offset : 0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(
    history ? history?.fetchCount : 25,
  );
  const [sortKey, setSortKey] = useState(
    history ? history?.sortKey : "created",
  );
  const [sortDirection, setSortDirection] = useState(
    history ? history?.sortDirection : "dsc",
  );
  const [showEmail, setShowEmail] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [searchValue, setSearchValue] = useState(
    history ? history?.searchValue : null,
  );
  const searchRef = useRef();
  const [showDateSelect, setShowDateSelect] = useState(false);
  const [rangeString, setRangeString] = useState("");
  const [range, setRange] = useState(
    history !== null && history?.range[0]?.startDate !== null
      ? [
          {
            startDate: new Date(history?.range[0]?.startDate),
            endDate: new Date(history?.range[0]?.endDate),
            key: history?.range?.key,
          },
        ]
      : [
          {
            startDate: null,
            endDate: null,
            key: "selection",
          },
        ],
  );

  const UpdateHistoryJSON = () => {
    if (
      range[0].startDate !== null &&
      moment(range[0].startDate).isBefore(moment("2000-01-01"))
    ) {
      setRange([
        {
          startDate: null,
          endDate: null,
          key: "selection",
        },
      ]);
      return;
    }

    var dateStr = "";
    if (range[0].startDate !== null) {
      dateStr = moment(range[0].startDate).format("YYYY-MM-DD");
    }
    if (range[0].endDate !== null) {
      dateStr += " -> ";
      dateStr += moment(range[0].endDate).format("YYYY-MM-DD");
    }
    setRangeString(dateStr);
    var saveStates = {};
    saveStates[historyKey] = JSON.stringify({
      range: range,
      sortKey: sortKey,
      sortDirection: sortDirection,
      offset: offset,
      fetchCount: fetchCount,
      searchValue: searchRef.current.value,
    });
    localStorage.setItem("history", JSON.stringify(saveStates));
  };

  useEffect(() => {
    document.title = "Emails";
  }, []);

  useEffect(() => {
    setOffset(0);
    setDataSource([]);
    setTotalPages(1);
    setTotalMessages(0);
    paginate();
    UpdateHistoryJSON();
  }, [range, offset, sortKey, sortDirection, searchValue]);

  const paginate = async () => {
    try {
      setLoading(true);

      var params = {};
      if (searchRef.current.value !== null && searchRef.current.value !== "") {
        params["search"] = searchRef.current.value;
      }
      if (
        range[0].endDate !== null &&
        range[0].endDate !== undefined &&
        range[0].startDate !== null &&
        range[0].startDate !== undefined
      ) {
        var st = range[0].startDate;
        var et = range[0].endDate;
        if (st.getTime() === et.getTime()) {
          et = addDays(et, 1);
        }
        params["startDate"] = moment(st).format("YYYY-MM-DD");
        params["endDate"] = moment(et).format("YYYY-MM-DD");
      }

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/emails/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotalMessages(0);
        }
        console.log("empty api call");
        setDataSource([]);
        return;
      }

      setDataSource(page.data.content);
      setTotalMessages(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const handleSetSortKey = (key) => {
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("dsc");
      } else {
        setSortDirection("asc");
      }
      return;
    }
    setSortKey(key);
  };

  const tableHeading = (key, name) => {
    if (key === sortKey) {
      return sortDirection === "dsc" ? name + " ↓" : name + " ↑";
    }
    return name;
  };

  const handleEmailOpen = (email) => {
    setEmailData(email);
    setShowEmail(true);
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover>
          <thead>
            <tr style={{ cursor: "pointer" }}>
              <th onClick={() => handleSetSortKey("subject")}>
                {tableHeading("subject", "Subject")}
              </th>
              <th>Associated Shift</th>
              <th onClick={() => handleSetSortKey("created")}>
                {tableHeading("created", "Created")}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((email) => (
              <tr key={email?.email?.id}>
                <td
                  className={"text-decoration-none pointer text-capitalize"}
                  onClick={() => handleEmailOpen(email?.email)}
                >
                  <span className={"text-axis-link fw-bold"}>
                    {email?.email?.subject}
                  </span>
                </td>
                <td>
                  {ShiftLinkExplicit(
                    email?.associatedShift?.shiftId,
                    email?.associatedShift?.notificationId,
                    email?.associatedShift?.table,
                  )}
                </td>
                <td>{AxisDateTimeFull(email?.email?.created)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {dataSource?.length}{" "}
            records(s) out of {totalMessages}
          </p>
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalMessages}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Row>
        <PageTitleController
          title={"Emails"}
          icon={<BsMailbox2Flag className={"icon-right-spacing"} />}
        />
      </Row>

      <Container fluid className={" m-0 p-0"}>
        <Row xs={"auto"} className={"align-items-center g-3"}>
          <Col xs={"auto"}>
            <input
              className={"fs-5 btn-w-px"}
              type="text"
              placeholder="search content"
              ref={searchRef}
              defaultValue={searchValue ? searchValue : null}
            />
          </Col>
          <Col xs={"auto"}>
            <div>
              <input
                className={"fs-5 btn-w-px"}
                type="text"
                placeholder="Date Range"
                defaultValue={rangeString}
                onClick={() => setShowDateSelect(true)}
              />
            </div>
          </Col>
          <Col xs={"auto"}>
            <Button
              variant={"outline-offblack"}
              onClick={() => setSearchValue(searchRef.current.value)}
            >
              Filter
            </Button>
          </Col>
        </Row>
      </Container>

      <br />

      {SpinnerOrData(loading)}

      <DateRangeSelectModal
        visible={showDateSelect}
        close={() => setShowDateSelect(false)}
        range={range}
        setRange={setRange}
      />
      <EmailModal
        visible={showEmail}
        close={() => setShowEmail(false)}
        email={emailData}
      />
    </Fragment>
  );
};

export default EmailTable;
